import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaBriefcase,
  FaCheckCircle,
  FaExclamationCircle,
  FaRegClock,
  FaStar,
} from "react-icons/fa";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCalendar,
  FiClock,
  FiEye,
  FiFilter,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiUsers,
} from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import {
  hydrateOpportunityApplicationCounts,
  saveOpportunityModerationLabel,
  subscribeToOpportunityBoard,
  updateOpportunityModeration,
} from "../services/opportunityAdminService";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b45309",
  danger: "#b91c1c",
  info: "#2563eb",
  muted: "#94a3b8",
};

const MODERATION_LABELS = ["clean", "spam", "fake", "duplicate", "expired", "other"];

const MODERATION_FILTER_OPTIONS = [
  { value: "all", label: "All moderation" },
  { value: "flagged", label: "Flagged only" },
  { value: "clean", label: "Clean only" },
  { value: "spam", label: "Spam" },
  { value: "fake", label: "Fake" },
  { value: "duplicate", label: "Duplicate" },
  { value: "expired", label: "Expired" },
  { value: "other", label: "Other" },
];

const QUEUE_OPTIONS = [
  { value: "needs_review", label: "Needs review" },
  { value: "hidden", label: "Hidden" },
  { value: "rejected", label: "Rejected" },
  { value: "approved", label: "Approved" },
  { value: "featured", label: "Featured" },
  { value: "all", label: "All opportunities" },
];

export default function Opportunities({ mode = "list" }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFromUrl = searchParams.get("opportunityId") || "";

  const employee = useMemo(() => safeGetEmployee(), []);
  const role = String(employee?.role || "").toLowerCase();
  const permissions = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const isSuperAdmin = role === "super_admin";
  const canView =
    isSuperAdmin ||
    permissions.some((permission) =>
      [
        "view_opportunities",
        "manage_opportunities",
        "moderate_opportunities",
        "feature_opportunities",
      ].includes(permission)
    );
  const canModerate =
    isSuperAdmin ||
    permissions.some((permission) =>
      ["manage_opportunities", "moderate_opportunities"].includes(permission)
    );
  const canFeature = canModerate || permissions.includes("feature_opportunities");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState(() => getInitialFilters(mode));
  const [applicationCounts, setApplicationCounts] = useState({});
  const [countBusy, setCountBusy] = useState(false);
  const [countRefreshNonce, setCountRefreshNonce] = useState(0);
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [moderationLabelDraft, setModerationLabelDraft] = useState("clean");

  const countsRef = useRef({});
  const opportunityIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const opportunityIdKey = useMemo(() => opportunityIds.join("|"), [opportunityIds]);
  const opportunityIdsRef = useRef([]);

  useEffect(() => {
    countsRef.current = applicationCounts;
  }, [applicationCounts]);

  useEffect(() => {
    opportunityIdsRef.current = opportunityIds;
  }, [opportunityIds]);

  useEffect(() => {
    setFilters(getInitialFilters(mode));
    setSearch("");
  }, [mode]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = subscribeToOpportunityBoard(
      (nextRows) => {
        setRows(nextRows);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Opportunity board stream failed:", err);
        setError(resolveFirestoreMessage(err, "Unable to load opportunities right now."));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [canView]);

  useEffect(() => {
    if (!canView) return undefined;

    if (!opportunityIdKey) {
      setApplicationCounts({});
      setCountBusy(false);
      return undefined;
    }

    let cancelled = false;

    async function loadCounts() {
      try {
        setCountBusy(true);

        const nextCounts = await hydrateOpportunityApplicationCounts(
          rows,
          countsRef.current,
          (partialCounts) => {
            if (cancelled) return;
            countsRef.current = partialCounts;
            setApplicationCounts(partialCounts);
          }
        );

        if (!cancelled) {
          countsRef.current = nextCounts;
          setApplicationCounts(nextCounts);
        }
      } catch (err) {
        console.error("Opportunity application counts failed:", err);
        if (!cancelled) {
          setToast({
            type: "error",
        message: "Response counts could not be refreshed.",
          });
        }
      } finally {
        if (!cancelled) setCountBusy(false);
      }
    }

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [canView, countRefreshNonce, opportunityIdKey, rows]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const categoryOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.category))],
    [rows]
  );
  const cityOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.city))],
    [rows]
  );
  const approvalOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.approvalStatus))],
    [rows]
  );
  const statusOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.status))],
    [rows]
  );

  const filteredRows = useMemo(() => {
    let list = [...rows];
    const queryText = deferredSearch.trim().toLowerCase();

    if (queryText) {
      list = list.filter((row) => row.searchText.includes(queryText));
    }

    if (filters.approval !== "all") {
      list = list.filter((row) => row.approvalStatus === filters.approval);
    }

    if (filters.status !== "all") {
      list = list.filter((row) => row.status === filters.status);
    }

    if (filters.featured !== "all") {
      list = list.filter((row) =>
        filters.featured === "featured" ? row.isFeatured : !row.isFeatured
      );
    }

    if (filters.category !== "all") {
      list = list.filter((row) => row.category === filters.category);
    }

    if (filters.city !== "all") {
      list = list.filter((row) => row.city === filters.city);
    }

    if (filters.moderation !== "all") {
      list = list.filter((row) => matchesModerationFilter(row, filters.moderation));
    }

    if (mode === "moderation" && filters.queue !== "all") {
      list = list.filter((row) => matchesQueueFilter(row, filters.queue));
    }

    return list.sort(mode === "moderation" ? sortForModeration : sortByNewest);
  }, [deferredSearch, filters, mode, rows]);

  useEffect(() => {
    if (mode !== "moderation") return;

    if (!filteredRows.length) {
      if (selectedFromUrl) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("opportunityId");
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    if (!selectedFromUrl || !filteredRows.some((row) => row.id === selectedFromUrl)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("opportunityId", filteredRows[0].id);
      setSearchParams(nextParams, { replace: true });
    }
  }, [filteredRows, mode, searchParams, selectedFromUrl, setSearchParams]);

  const selectedOpportunity = useMemo(() => {
    if (mode !== "moderation") return null;
    if (!selectedFromUrl) return filteredRows[0] || null;
    return rows.find((row) => row.id === selectedFromUrl) || filteredRows[0] || null;
  }, [filteredRows, mode, rows, selectedFromUrl]);

  useEffect(() => {
    if (!selectedOpportunity) {
      setModerationLabelDraft("clean");
      return;
    }

    setModerationLabelDraft(selectedOpportunity.moderationLabel || "clean");
  }, [selectedOpportunity]);

  const stats = useMemo(() => {
    const totalApplications = rows.reduce(
      (sum, row) => sum + Number(applicationCounts[row.id] || 0),
      0
    );

    return {
      total: rows.length,
      pending: rows.filter((row) => row.approvalStatus === "pending").length,
      hidden: rows.filter((row) => row.status === "hidden").length,
      featured: rows.filter((row) => row.isFeatured).length,
      flagged: rows.filter((row) => Boolean(row.moderationLabel)).length,
      totalApplications,
    };
  }, [applicationCounts, rows]);

  const adminContext = useMemo(
    () => ({
      docId: String(employee?.docId || ""),
      name:
        employee?.fullName ||
        employee?.full_name ||
        employee?.name ||
        employee?.mobile ||
        "Admin",
      role: String(employee?.role || ""),
    }),
    [employee]
  );

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setSearch("");
    setFilters(getInitialFilters(mode));
  }

  function goToModeration(opportunityId) {
    if (!opportunityId) {
      navigate("/panel/opportunities/moderation");
      return;
    }

    navigate(`/panel/opportunities/moderation?opportunityId=${encodeURIComponent(opportunityId)}`);
  }

  async function runOpportunityAction(opportunity, action, options = {}) {
    if (!opportunity?.id) return;

    const isFeatureAction = action === "feature" || action === "unfeature";

    if (isFeatureAction && !canFeature) {
      setToast({
        type: "error",
        message: "You do not have permission to change featured status.",
      });
      return;
    }

    if (!isFeatureAction && !canModerate) {
      setToast({
        type: "error",
        message: "You do not have permission to moderate opportunities.",
      });
      return;
    }

    const busyKey = `${opportunity.id}:${action}`;

    try {
      setActionBusyKey(busyKey);

      await updateOpportunityModeration(opportunity, action, {
        moderationLabel: options.includeDraftLabel ? moderationLabelDraft : options.moderationLabel,
        adminContext,
      });

      setConfirm(null);
      setToast({
        type: "success",
        message: buildActionSuccessMessage(action, opportunity.title),
      });
    } catch (err) {
      console.error(`Opportunity ${action} failed:`, err);
      setToast({
        type: "error",
        message: resolveFirestoreMessage(
          err,
          `The opportunity could not be ${action === "hide" ? "hidden" : `${action}d`}.`
        ),
      });
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleSaveModerationLabel(opportunity) {
    if (!opportunity?.id) return;

    if (!canModerate) {
      setToast({
        type: "error",
        message: "You do not have permission to update moderation tags.",
      });
      return;
    }

    const busyKey = `${opportunity.id}:label`;

    try {
      setActionBusyKey(busyKey);
      await saveOpportunityModerationLabel(
        opportunity,
        moderationLabelDraft,
        adminContext
      );
      setToast({
        type: "success",
        message: `Moderation tag saved as ${formatLabel(moderationLabelDraft)}.`,
      });
    } catch (err) {
      console.error("Opportunity moderation label failed:", err);
      setToast({
        type: "error",
        message: resolveFirestoreMessage(err, "The moderation tag could not be saved."),
      });
    } finally {
      setActionBusyKey("");
    }
  }

  function queueConfirm(opportunity, action, options = {}) {
    setConfirm({
      title: `${toTitleCase(action)} Opportunity`,
      message: buildActionConfirmMessage(
        action,
        opportunity.title,
        options.includeDraftLabel ? moderationLabelDraft : ""
      ),
      confirmText: toTitleCase(action),
      run: () => runOpportunityAction(opportunity, action, options),
    });
  }

  if (!canView) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={34} color="#e53935" />
          <h3 style={{ margin: "1rem 0 0.35rem", color: COLORS.text }}>Access Denied</h3>
          <p style={{ margin: 0, color: COLORS.subtext }}>
            You do not have permission to open the Opportunity Board.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={heroCard}>
        <div style={heroCopy}>
          <div style={heroIcon}>
            <FaBriefcase />
          </div>
          <div>
            <h2 style={heroTitle}>
              {mode === "moderation" ? "Opportunity Moderation" : "Opportunity Board"}
            </h2>
            <p style={heroSub}>
              {mode === "moderation"
                ? "Review suspicious, hidden, or pending opportunities, classify spam or fake listings, and take moderation actions in one place."
                : "Track all live opportunity posts across organizers and users, audit approvals, and keep featured listings under control."}
            </p>
          </div>
        </div>

        <div style={statsGrid}>
          <StatCard label="Total" value={stats.total} tone="neutral" />
          <StatCard label="Pending" value={stats.pending} tone="warn" />
          <StatCard label="Hidden" value={stats.hidden} tone="danger" />
          <StatCard label="Featured" value={stats.featured} tone="brand" />
          <StatCard label="Flagged" value={stats.flagged} tone="warn" />
          <StatCard label="Responses" value={stats.totalApplications} tone="info" />
        </div>
      </div>

      <div style={tabRow}>
        <button
          type="button"
          style={mode === "list" ? tabButtonActive : tabButton}
          onClick={() => navigate("/panel/opportunities")}
        >
          <FaBriefcase />
          <span>Opportunity Board</span>
        </button>
        <button
          type="button"
          style={mode === "moderation" ? tabButtonActive : tabButton}
          onClick={() => navigate("/panel/opportunities/moderation")}
        >
          <FiAlertTriangle />
          <span>Moderation Queue</span>
        </button>
      </div>

      <div style={controlsCard}>
        <div style={controlsRow}>
          <div style={{ ...searchBox, flex: 1 }}>
            <FiSearch style={{ color: COLORS.subtext }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, organizer, city, category, venue, skill, approval, status..."
              style={searchInput}
            />
          </div>

          <button
            type="button"
            style={ghostButton}
            onClick={() => setCountRefreshNonce((value) => value + 1)}
          >
            <FiRefreshCw />
            <span>{countBusy ? "Refreshing..." : "Refresh counts"}</span>
          </button>

          <button type="button" style={ghostButton} onClick={resetFilters}>
            <FiFilter />
            <span>Reset filters</span>
          </button>
        </div>

        <div style={filterGrid}>
          <FilterSelect
            label="Approval"
            value={filters.approval}
            onChange={(value) => updateFilter("approval", value)}
            options={approvalOptions}
          />
          <FilterSelect
            label="Visibility"
            value={filters.status}
            onChange={(value) => updateFilter("status", value)}
            options={statusOptions}
          />
          <FilterSelect
            label="Featured"
            value={filters.featured}
            onChange={(value) => updateFilter("featured", value)}
            options={["all", "featured", "standard"]}
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            onChange={(value) => updateFilter("category", value)}
            options={categoryOptions}
          />
          <FilterSelect
            label="City"
            value={filters.city}
            onChange={(value) => updateFilter("city", value)}
            options={cityOptions}
          />
          <FilterSelect
            label="Moderation"
            value={filters.moderation}
            onChange={(value) => updateFilter("moderation", value)}
            options={MODERATION_FILTER_OPTIONS}
          />
          {mode === "moderation" ? (
            <FilterSelect
              label="Queue"
              value={filters.queue}
              onChange={(value) => updateFilter("queue", value)}
              options={QUEUE_OPTIONS}
            />
          ) : null}
        </div>
      </div>

      {error ? (
        <div style={errorCard}>
          <FaExclamationCircle />
          <span>{error}</span>
        </div>
      ) : null}

      {mode === "moderation" ? (
        <div style={moderationLayout}>
          <div style={queueCard}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Review Queue</div>
                <div style={sectionCopy}>
                  {filteredRows.length} opportunity{filteredRows.length === 1 ? "" : "ies"} in the
                  current queue.
                </div>
              </div>
              <Badge tone="neutral">{countBusy ? "Counts syncing" : "Live"}</Badge>
            </div>

            {loading ? (
              <EmptyState
                title="Loading opportunities"
                copy="Pulling the latest moderation queue from Firestore."
              />
            ) : filteredRows.length === 0 ? (
              <EmptyState
                title="No opportunities match these filters"
                copy="Try broadening the queue or moderation filters to review more listings."
              />
            ) : (
              <div style={queueList}>
                {filteredRows.map((opportunity) => {
                  const isSelected = selectedOpportunity?.id === opportunity.id;
                  const applications = applicationCounts[opportunity.id];
                  return (
                    <button
                      type="button"
                      key={opportunity.id}
                      style={isSelected ? queueItemActive : queueItem}
                      onClick={() => goToModeration(opportunity.id)}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={queueTitle}>{opportunity.title}</div>
                          <div style={queueMeta}>
                            {opportunity.organizerName} • {opportunity.city}
                          </div>
                        </div>
                        <FiArrowRight style={{ flexShrink: 0 }} />
                      </div>

                      <div style={queueBadgeRow}>
                        <Badge tone={badgeToneForApproval(opportunity.approvalStatus)}>
                          {formatLabel(opportunity.approvalStatus)}
                        </Badge>
                        <Badge tone={badgeToneForStatus(opportunity.status)}>
                          {formatLabel(opportunity.status)}
                        </Badge>
                        {opportunity.moderationLabel ? (
                          <Badge tone="danger">{formatLabel(opportunity.moderationLabel)}</Badge>
                        ) : (
                          <Badge tone="ok">Clean</Badge>
                        )}
                      </div>

                      <div style={queueFoot}>
                        <span>{formatDateTime(opportunity.createdAt)}</span>
                        <span>
                          {applications === undefined
                            ? "Responses loading..."
                            : `${applications} ${opportunity.metricLabel || "applications"}`}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={detailCard}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Opportunity Details</div>
                <div style={sectionCopy}>
                  Review the listing, classify the risk level, and take moderation actions.
                </div>
              </div>
              {selectedOpportunity ? (
                <button
                  type="button"
                  style={ghostButton}
                  onClick={() => navigate("/panel/opportunities")}
                >
                  <FiEye />
                  <span>Open list view</span>
                </button>
              ) : null}
            </div>

            {loading ? (
              <EmptyState
                title="Loading details"
                copy="The selected opportunity will appear here as soon as the list finishes syncing."
              />
            ) : !selectedOpportunity ? (
              <EmptyState
                title="No opportunity selected"
                copy="Pick an opportunity from the review queue to open its moderation workspace."
              />
            ) : (
              <div style={detailBody}>
                <div style={detailHero}>
                  <div>
                    <h3 style={detailTitle}>{selectedOpportunity.title}</h3>
                    <p style={detailSub}>
                      {selectedOpportunity.organizerName} • {selectedOpportunity.category} • {selectedOpportunity.city}
                    </p>
                  </div>
                  <div style={detailBadgeWrap}>
                    <Badge tone={badgeToneForApproval(selectedOpportunity.approvalStatus)}>
                      {formatLabel(selectedOpportunity.approvalStatus)}
                    </Badge>
                    <Badge tone={badgeToneForStatus(selectedOpportunity.status)}>
                      {formatLabel(selectedOpportunity.status)}
                    </Badge>
                    <Badge tone={selectedOpportunity.isFeatured ? "brand" : "neutral"}>
                      {selectedOpportunity.isFeatured ? "Featured" : "Standard"}
                    </Badge>
                    <Badge tone={selectedOpportunity.sourceCollection === "new_oppurtunity" ? "info" : "neutral"}>
                      {selectedOpportunity.sourceLabel}
                    </Badge>
                    <Badge tone={selectedOpportunity.moderationLabel ? "danger" : "ok"}>
                      {selectedOpportunity.moderationLabel
                        ? formatLabel(selectedOpportunity.moderationLabel)
                        : "Clean"}
                    </Badge>
                  </div>
                </div>

                <div style={actionPanel}>
                  <div style={sectionTitle}>Moderation Actions</div>
                  <div style={actionGrid}>
                    <ActionButton
                      label={actionBusyKey === `${selectedOpportunity.id}:approve` ? "Approving..." : "Approve"}
                      tone="ok"
                      disabled={!canModerate || actionBusyKey.startsWith(`${selectedOpportunity.id}:`)}
                      onClick={() =>
                        queueConfirm(selectedOpportunity, "approve", { includeDraftLabel: true })
                      }
                    />
                    <ActionButton
                      label={actionBusyKey === `${selectedOpportunity.id}:reject` ? "Rejecting..." : "Reject"}
                      tone="danger"
                      disabled={!canModerate || actionBusyKey.startsWith(`${selectedOpportunity.id}:`)}
                      onClick={() =>
                        queueConfirm(selectedOpportunity, "reject", { includeDraftLabel: true })
                      }
                    />
                    <ActionButton
                      label={actionBusyKey === `${selectedOpportunity.id}:hide` ? "Hiding..." : "Hide"}
                      tone="warn"
                      disabled={!canModerate || actionBusyKey.startsWith(`${selectedOpportunity.id}:`)}
                      onClick={() =>
                        queueConfirm(selectedOpportunity, "hide", { includeDraftLabel: true })
                      }
                    />
                    <ActionButton
                      label={
                        actionBusyKey ===
                        `${selectedOpportunity.id}:${selectedOpportunity.isFeatured ? "unfeature" : "feature"}`
                          ? "Updating..."
                          : selectedOpportunity.isFeatured
                          ? "Unfeature"
                          : "Feature"
                      }
                      tone="brand"
                      disabled={!canFeature || actionBusyKey.startsWith(`${selectedOpportunity.id}:`)}
                      onClick={() =>
                        runOpportunityAction(
                          selectedOpportunity,
                          selectedOpportunity.isFeatured ? "unfeature" : "feature",
                          { includeDraftLabel: false }
                        )
                      }
                    />
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div style={fieldLabel}>Moderation label</div>
                    <div style={labelGrid}>
                      {MODERATION_LABELS.map((label) => {
                        const active = (moderationLabelDraft || "clean") === label;
                        return (
                          <button
                            key={label}
                            type="button"
                            style={active ? moderationTagActive : moderationTag}
                            onClick={() => setModerationLabelDraft(label)}
                          >
                            {formatLabel(label)}
                          </button>
                        );
                      })}
                    </div>
                    <div style={fieldHint}>
                      Use <strong>Clean</strong> to clear a spam or fake marker. Any moderation action
                      above will save the currently selected label.
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                      <button
                        type="button"
                        style={primaryButton}
                        disabled={!canModerate || actionBusyKey.startsWith(`${selectedOpportunity.id}:`)}
                        onClick={() => handleSaveModerationLabel(selectedOpportunity)}
                      >
                        {actionBusyKey === `${selectedOpportunity.id}:label`
                          ? "Saving..."
                          : "Save moderation tag"}
                      </button>
                      <button
                        type="button"
                        style={ghostButton}
                        onClick={() =>
                          setModerationLabelDraft(
                            selectedOpportunity.moderationLabel || "clean"
                          )
                        }
                      >
                        Reset tag
                      </button>
                    </div>
                  </div>
                </div>

                <div style={detailsGrid}>
                  <DetailRow
                    icon={<FiUsers />}
                    label="Organizer"
                    value={selectedOpportunity.organizerName}
                  />
                  <DetailRow
                    icon={<FiMapPin />}
                    label="City"
                    value={selectedOpportunity.city}
                  />
                  <DetailRow
                    icon={<FiTag />}
                    label="Category"
                    value={selectedOpportunity.category}
                  />
                  <DetailRow
                    icon={<FiCalendar />}
                    label="Event Date"
                    value={formatDateTime(selectedOpportunity.eventDate)}
                  />
                  <DetailRow
                    icon={<FiClock />}
                    label="Created"
                    value={formatDateTime(selectedOpportunity.createdAt)}
                  />
                  <DetailRow
                    icon={<FaRegClock />}
                    label="Expires"
                    value={formatDateTime(selectedOpportunity.expiresAt)}
                  />
                  <DetailRow
                    icon={<FaStar />}
                    label="Budget"
                    value={selectedOpportunity.budgetLabel}
                  />
                  <DetailRow
                    icon={<FiUsers />}
                    label={selectedOpportunity.metricLabel === "bids" ? "Bids" : "Applications"}
                    value={
                      applicationCounts[selectedOpportunity.id] === undefined
                        ? "Loading..."
                        : `${applicationCounts[selectedOpportunity.id]} total`
                    }
                  />
                  <DetailRow
                    icon={<FiTag />}
                    label="Source"
                    value={selectedOpportunity.sourceLabel}
                  />
                  <DetailRow
                    icon={<FaCheckCircle />}
                    label="Payment"
                    value={formatLabel(selectedOpportunity.paymentStatus)}
                  />
                  <DetailRow
                    icon={<FiClock />}
                    label="Task"
                    value={formatLabel(selectedOpportunity.taskStatus)}
                  />
                  <DetailRow
                    icon={<FaStar />}
                    label="Accepted"
                    value={
                      selectedOpportunity.acceptedAmount
                        ? `Rs ${formatCompactMoney(selectedOpportunity.acceptedAmount)}`
                        : "Not selected"
                    }
                  />
                </div>

                <div style={infoPanel}>
                  <div style={infoTitle}>Venue</div>
                  <div style={infoCopy}>
                    {selectedOpportunity.venueName || "Venue not provided"}
                  </div>
                </div>

                <div style={infoPanel}>
                  <div style={infoTitle}>Description</div>
                  <div style={infoCopy}>
                    {selectedOpportunity.description || "No description added for this opportunity."}
                  </div>
                </div>

                <div style={infoPanel}>
                  <div style={infoTitle}>Required Skills</div>
                  {selectedOpportunity.requiredSkills?.length ? (
                    <div style={skillWrap}>
                      {selectedOpportunity.requiredSkills.map((skill) => (
                        <Badge key={skill} tone="neutral">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div style={infoCopy}>No required skills listed.</div>
                  )}
                </div>

                <div style={infoPanel}>
                  <div style={infoTitle}>Record ID</div>
                  <code style={codeBlock}>
                    {selectedOpportunity.sourceCollection}/{selectedOpportunity.id}
                  </code>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={tableCard}>
          <div style={sectionHead}>
            <div>
              <div style={sectionTitle}>All Opportunities</div>
              <div style={sectionCopy}>
                {filteredRows.length} opportunity{filteredRows.length === 1 ? "" : "ies"} match the
                active filters.
              </div>
            </div>
            <Badge tone="neutral">{countBusy ? "Counts syncing" : "Realtime sync"}</Badge>
          </div>

          {loading ? (
            <EmptyState
              title="Loading opportunities"
              copy="Syncing all opportunity posts from Firestore."
            />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title="No opportunities found"
              copy="Try a different search term or clear a few filters to widen the board."
            />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Title</Th>
                    <Th>Organizer</Th>
                    <Th>City</Th>
                    <Th>Category</Th>
                    <Th>Event Date</Th>
                    <Th>Budget</Th>
                    <Th>Created</Th>
                    <Th>Status</Th>
                    <Th>Approval</Th>
                    <Th>Responses</Th>
                    <Th>Featured</Th>
                    <Th>Moderation</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((opportunity, index) => {
                    const rowBusy = actionBusyKey.startsWith(`${opportunity.id}:`);
                    const applications = applicationCounts[opportunity.id];
                    const rowBackground = index % 2 ? "#fafafa" : "#ffffff";
                    return (
                      <tr key={opportunity.id} style={{ background: rowBackground }}>
                        <Td>
                          <div style={{ fontWeight: 800, color: COLORS.text }}>
                            {opportunity.title}
                          </div>
                          <div style={{ marginTop: 4, color: COLORS.subtext, fontSize: 12 }}>
                            {opportunity.sourceLabel} - {opportunity.venueName || "Venue not added"}
                          </div>
                        </Td>
                        <Td>{opportunity.organizerName}</Td>
                        <Td>{opportunity.city}</Td>
                        <Td>{opportunity.category}</Td>
                        <Td>{formatDateTime(opportunity.eventDate)}</Td>
                        <Td>{opportunity.budgetLabel}</Td>
                        <Td>{formatDateTime(opportunity.createdAt)}</Td>
                        <Td>
                          <Badge tone={badgeToneForStatus(opportunity.status)}>
                            {formatLabel(opportunity.status)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge tone={badgeToneForApproval(opportunity.approvalStatus)}>
                            {formatLabel(opportunity.approvalStatus)}
                          </Badge>
                        </Td>
                        <Td>
                          {applications === undefined ? (
                            <span style={{ color: COLORS.subtext }}>Loading...</span>
                          ) : (
                            `${applications} ${opportunity.metricLabel || "applications"}`
                          )}
                        </Td>
                        <Td>
                          <Badge tone={opportunity.isFeatured ? "brand" : "neutral"}>
                            {opportunity.isFeatured ? "Featured" : "Standard"}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge tone={opportunity.moderationLabel ? "danger" : "ok"}>
                            {opportunity.moderationLabel
                              ? formatLabel(opportunity.moderationLabel)
                              : "Clean"}
                          </Badge>
                        </Td>
                        <Td>
                          <div style={tableActionWrap}>
                            <button
                              type="button"
                              style={smallActionOk}
                              disabled={!canModerate || rowBusy}
                              onClick={() => queueConfirm(opportunity, "approve")}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              style={smallActionDanger}
                              disabled={!canModerate || rowBusy}
                              onClick={() => queueConfirm(opportunity, "reject")}
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              style={smallActionWarn}
                              disabled={!canModerate || rowBusy}
                              onClick={() => queueConfirm(opportunity, "hide")}
                            >
                              Hide
                            </button>
                            <button
                              type="button"
                              style={smallActionBrand}
                              disabled={!canFeature || rowBusy}
                              onClick={() =>
                                runOpportunityAction(
                                  opportunity,
                                  opportunity.isFeatured ? "unfeature" : "feature"
                                )
                              }
                            >
                              {opportunity.isFeatured ? "Unfeature" : "Feature"}
                            </button>
                            <button
                              type="button"
                              style={smallActionGhost}
                              onClick={() => goToModeration(opportunity.id)}
                            >
                              Moderate
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Confirm"}
        loading={Boolean(confirm && actionBusyKey)}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.run?.()}
      />

      {toast ? (
        <div style={toast.type === "error" ? toastError : toastSuccess}>
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span>{toast.message}</span>
        </div>
      ) : null}
    </div>
  );
}

function FilterSelect({ label, options, value, onChange }) {
  const normalizedOptions = options.map((option) =>
    typeof option === "string"
      ? { value: option, label: option === "all" ? "All" : formatLabel(option) }
      : option
  );

  return (
    <label style={filterLabel}>
      <span style={filterCaption}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} style={filterSelect}>
        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const palette = {
    neutral: { background: "#ffffff", border: "#e2e8f0", color: "#0f172a" },
    warn: { background: "#fff7ed", border: "#fdba74", color: "#9a3412" },
    danger: { background: "#fef2f2", border: "#fecaca", color: "#991b1b" },
    brand: { background: "#fff1f7", border: "#f9a8d4", color: "#9d174d" },
    info: { background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
  }[tone];

  return (
    <div
      style={{
        background: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: "0.85rem 0.95rem",
      }}
    >
      <div style={{ color: COLORS.subtext, fontSize: 12, fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: 6, color: palette.color, fontSize: 22, fontWeight: 900 }}>
        {value}
      </div>
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const palette = {
    neutral: { background: "#f8fafc", color: "#334155", border: "#e2e8f0" },
    ok: { background: "#ecfdf5", color: "#166534", border: "#bbf7d0" },
    warn: { background: "#fff7ed", color: "#b45309", border: "#fdba74" },
    danger: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    brand: { background: "#fff1f7", color: "#be185d", border: "#f9a8d4" },
    info: { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  }[tone];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  );
}

function ActionButton({ label, onClick, disabled, tone = "neutral" }) {
  const palette = {
    ok: { background: "#16a34a", color: "#ffffff" },
    danger: { background: "#dc2626", color: "#ffffff" },
    warn: { background: "#ea580c", color: "#ffffff" },
    brand: { background: "linear-gradient(90deg,#f20c53,#fb6c33)", color: "#ffffff" },
    neutral: { background: "#ffffff", color: "#0f172a" },
  }[tone];

  return (
    <button
      type="button"
      style={{
        minHeight: 42,
        borderRadius: 12,
        border: tone === "neutral" ? `1px solid ${COLORS.border}` : "none",
        background: disabled ? "#e2e8f0" : palette.background,
        color: disabled ? "#64748b" : palette.color,
        padding: "0.75rem 0.9rem",
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div style={detailRow}>
      <div style={detailRowLabel}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={detailRowValue}>{value || "Not available"}</div>
    </div>
  );
}

function EmptyState({ title, copy }) {
  return (
    <div style={emptyState}>
      <div style={emptyStateIcon}>
        <FiAlertTriangle />
      </div>
      <div style={{ fontWeight: 900, color: COLORS.text }}>{title}</div>
      <div style={{ color: COLORS.subtext, maxWidth: 440 }}>{copy}</div>
    </div>
  );
}

function Th({ children }) {
  return <th style={tableHead}>{children}</th>;
}

function Td({ children }) {
  return <td style={tableCell}>{children}</td>;
}

function getInitialFilters(mode) {
  return {
    approval: mode === "moderation" ? "pending" : "all",
    status: "all",
    featured: "all",
    category: "all",
    city: "all",
    moderation: "all",
    queue: mode === "moderation" ? "needs_review" : "all",
  };
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function formatDateTime(value) {
  if (!value) return "Not set";

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);

    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "Not set";

    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return "Not set";
  }
}

function formatCompactMoney(amount) {
  try {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return String(amount || 0);
  }
}

function sortByNewest(a, b) {
  return (b.createdAtMs || 0) - (a.createdAtMs || 0);
}

function sortForModeration(a, b) {
  const priorityDiff = moderationPriority(a) - moderationPriority(b);
  if (priorityDiff !== 0) return priorityDiff;
  return sortByNewest(a, b);
}

function moderationPriority(opportunity) {
  if (opportunity.approvalStatus === "pending") return 0;
  if (opportunity.moderationLabel) return 1;
  if (opportunity.status === "hidden") return 2;
  if (opportunity.approvalStatus === "rejected") return 3;
  if (opportunity.isFeatured) return 4;
  return 5;
}

function matchesModerationFilter(opportunity, filter) {
  if (filter === "flagged") return Boolean(opportunity.moderationLabel);
  if (filter === "clean") return !opportunity.moderationLabel;
  return opportunity.moderationLabel === filter;
}

function matchesQueueFilter(opportunity, filter) {
  switch (filter) {
    case "needs_review":
      return (
        opportunity.approvalStatus === "pending" ||
        opportunity.status === "hidden" ||
        Boolean(opportunity.moderationLabel)
      );
    case "hidden":
      return opportunity.status === "hidden";
    case "rejected":
      return opportunity.approvalStatus === "rejected";
    case "approved":
      return opportunity.approvalStatus === "approved";
    case "featured":
      return opportunity.isFeatured;
    default:
      return true;
  }
}

function formatLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "Not set";
  return text.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function toTitleCase(value) {
  return formatLabel(value);
}

function buildActionConfirmMessage(action, title, moderationLabel) {
  const labelNote =
    moderationLabel && moderationLabel !== "clean"
      ? ` The moderation tag "${formatLabel(moderationLabel)}" will also be saved.`
      : moderationLabel === "clean"
      ? " The moderation tag will be cleared to Clean."
      : "";

  switch (action) {
    case "approve":
      return `Approve "${title}" and make it eligible for the opportunity board?${labelNote}`;
    case "reject":
      return `Reject "${title}" from the opportunity board?${labelNote}`;
    case "hide":
      return `Hide "${title}" from visibility in the app?${labelNote}`;
    default:
      return `Apply "${action}" to "${title}"?${labelNote}`;
  }
}

function buildActionSuccessMessage(action, title) {
  switch (action) {
    case "approve":
      return `"${title}" was approved.`;
    case "reject":
      return `"${title}" was rejected.`;
    case "hide":
      return `"${title}" was hidden.`;
    case "feature":
      return `"${title}" is now featured.`;
    case "unfeature":
      return `"${title}" is no longer featured.`;
    default:
      return `"${title}" was updated.`;
  }
}

function resolveFirestoreMessage(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code === "permission-denied") {
    return "Firestore denied this action. Please confirm the signed-in admin account has the right access.";
  }
  if (code === "unavailable") {
    return "Firestore is temporarily unavailable. Please try again in a moment.";
  }
  return fallback || error?.message || "Something went wrong.";
}

function uniqueValues(list) {
  return Array.from(new Set((list || []).filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b))
  );
}

function badgeToneForApproval(status) {
  if (status === "approved") return "ok";
  if (status === "rejected") return "danger";
  if (status === "pending") return "warn";
  return "neutral";
}

function badgeToneForStatus(status) {
  if (status === "hidden") return "danger";
  if (status === "active" || status === "live" || status === "open") return "ok";
  if (status === "closed" || status === "expired") return "warn";
  return "neutral";
}

const pageWrap = {
  padding: 16,
  background: COLORS.page,
  minHeight: "100vh",
  fontFamily: "Plus Jakarta Sans, system-ui, sans-serif",
};

const heroCard = {
  display: "grid",
  gap: 16,
  padding: "1.1rem",
  borderRadius: 22,
  border: `1px solid ${COLORS.border}`,
  background: "linear-gradient(135deg, rgba(255,255,255,.98), rgba(255,245,246,.96))",
  boxShadow: "0 10px 28px rgba(15,23,42,.08)",
};

const heroCopy = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
};

const heroIcon = {
  width: 52,
  height: 52,
  borderRadius: 16,
  display: "grid",
  placeItems: "center",
  color: "#ffffff",
  background: "linear-gradient(135deg,#f20c53,#fb6c33)",
  boxShadow: "0 16px 24px rgba(242,12,83,.2)",
  fontSize: 22,
};

const heroTitle = {
  margin: 0,
  fontSize: "1.8rem",
  fontWeight: 900,
  color: COLORS.text,
};

const heroSub = {
  margin: "6px 0 0",
  maxWidth: 760,
  color: COLORS.subtext,
  lineHeight: 1.6,
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 10,
};

const tabRow = {
  marginTop: 14,
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const tabButton = {
  minHeight: 42,
  padding: "0.8rem 1rem",
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
};

const tabButtonActive = {
  ...tabButton,
  border: "none",
  color: "#ffffff",
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
  boxShadow: "0 12px 22px rgba(242,12,83,.18)",
};

const controlsCard = {
  marginTop: 14,
  padding: "1rem",
  borderRadius: 20,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  boxShadow: "0 10px 26px rgba(15,23,42,.06)",
};

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const searchBox = {
  minWidth: 280,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0.85rem 0.95rem",
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
};

const searchInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 14,
  background: "transparent",
};

const primaryButton = {
  minHeight: 42,
  padding: "0.75rem 1rem",
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
  color: "#ffffff",
  fontWeight: 800,
  cursor: "pointer",
};

const ghostButton = {
  minHeight: 42,
  padding: "0.75rem 1rem",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
};

const filterGrid = {
  marginTop: 12,
  display: "grid",
  gap: 12,
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
};

const filterLabel = {
  display: "grid",
  gap: 6,
};

const filterCaption = {
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 700,
};

const filterSelect = {
  minHeight: 42,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0 12px",
};

const errorCard = {
  marginTop: 14,
  borderRadius: 16,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#991b1b",
  padding: "0.9rem 1rem",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontWeight: 700,
};

const moderationLayout = {
  marginTop: 14,
  display: "grid",
  gap: 14,
  gridTemplateColumns: "minmax(320px, 360px) minmax(0, 1fr)",
  alignItems: "start",
};

const queueCard = {
  borderRadius: 20,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  boxShadow: "0 10px 26px rgba(15,23,42,.06)",
  padding: "1rem",
};

const detailCard = {
  borderRadius: 20,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  boxShadow: "0 10px 26px rgba(15,23,42,.06)",
  padding: "1rem",
};

const sectionHead = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const sectionTitle = {
  fontSize: 18,
  fontWeight: 900,
  color: COLORS.text,
};

const sectionCopy = {
  marginTop: 4,
  color: COLORS.subtext,
  lineHeight: 1.55,
};

const queueList = {
  marginTop: 14,
  display: "grid",
  gap: 10,
  maxHeight: "calc(100vh - 360px)",
  overflowY: "auto",
  paddingRight: 4,
};

const queueItem = {
  width: "100%",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0.95rem",
  textAlign: "left",
  cursor: "pointer",
};

const queueItemActive = {
  ...queueItem,
  borderColor: "rgba(242,12,83,.38)",
  background: "linear-gradient(135deg, rgba(255,241,247,.92), rgba(255,255,255,.98))",
  boxShadow: "0 12px 24px rgba(242,12,83,.12)",
};

const queueTitle = {
  fontSize: 15,
  fontWeight: 900,
  color: COLORS.text,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const queueMeta = {
  marginTop: 4,
  color: COLORS.subtext,
  fontSize: 13,
};

const queueBadgeRow = {
  marginTop: 10,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const queueFoot = {
  marginTop: 10,
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  color: COLORS.subtext,
  fontSize: 12,
  flexWrap: "wrap",
};

const detailBody = {
  marginTop: 14,
  display: "grid",
  gap: 14,
};

const detailHero = {
  display: "flex",
  justifyContent: "space-between",
  gap: 14,
  flexWrap: "wrap",
  paddingBottom: 12,
  borderBottom: `1px solid ${COLORS.border}`,
};

const detailTitle = {
  margin: 0,
  fontSize: "1.45rem",
  fontWeight: 900,
  color: COLORS.text,
};

const detailSub = {
  margin: "6px 0 0",
  color: COLORS.subtext,
};

const detailBadgeWrap = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "flex-start",
};

const actionPanel = {
  borderRadius: 18,
  border: `1px solid ${COLORS.border}`,
  background: "#fffafc",
  padding: "1rem",
};

const actionGrid = {
  marginTop: 12,
  display: "grid",
  gap: 10,
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
};

const fieldLabel = {
  color: COLORS.text,
  fontSize: 14,
  fontWeight: 800,
};

const fieldHint = {
  marginTop: 8,
  color: COLORS.subtext,
  fontSize: 13,
  lineHeight: 1.55,
};

const labelGrid = {
  marginTop: 10,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const moderationTag = {
  minHeight: 36,
  padding: "0.55rem 0.85rem",
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  fontWeight: 800,
  cursor: "pointer",
};

const moderationTagActive = {
  ...moderationTag,
  borderColor: "transparent",
  color: "#ffffff",
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
};

const detailsGrid = {
  display: "grid",
  gap: 12,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const detailRow = {
  borderRadius: 16,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  padding: "0.9rem",
};

const detailRowLabel = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: COLORS.subtext,
  fontSize: 13,
  fontWeight: 700,
};

const detailRowValue = {
  marginTop: 8,
  color: COLORS.text,
  fontSize: 15,
  fontWeight: 800,
  lineHeight: 1.5,
};

const infoPanel = {
  borderRadius: 18,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  padding: "1rem",
};

const infoTitle = {
  color: COLORS.text,
  fontSize: 15,
  fontWeight: 900,
};

const infoCopy = {
  marginTop: 8,
  color: COLORS.subtext,
  lineHeight: 1.7,
  whiteSpace: "pre-wrap",
};

const skillWrap = {
  marginTop: 10,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const codeBlock = {
  display: "inline-block",
  marginTop: 8,
  padding: "0.65rem 0.8rem",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#f8fafc",
  color: COLORS.text,
  wordBreak: "break-all",
};

const tableCard = {
  marginTop: 14,
  borderRadius: 20,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  boxShadow: "0 10px 26px rgba(15,23,42,.06)",
  padding: "1rem",
};

const tableStyle = {
  width: "100%",
  minWidth: 1420,
  borderCollapse: "collapse",
  fontSize: 14,
};

const tableHead = {
  padding: "0.85rem 0.75rem",
  textAlign: "left",
  color: COLORS.text,
  fontWeight: 900,
  letterSpacing: "0.02em",
  borderBottom: `1px solid ${COLORS.border}`,
  background: "#fff7f9",
  whiteSpace: "nowrap",
};

const tableCell = {
  padding: "0.9rem 0.75rem",
  borderBottom: `1px solid ${COLORS.border}`,
  verticalAlign: "top",
  color: COLORS.text,
};

const tableActionWrap = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  minWidth: 280,
};

const smallActionBase = {
  minHeight: 34,
  padding: "0.45rem 0.75rem",
  borderRadius: 10,
  border: "none",
  color: "#ffffff",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
};

const smallActionOk = {
  ...smallActionBase,
  background: "#16a34a",
};

const smallActionDanger = {
  ...smallActionBase,
  background: "#dc2626",
};

const smallActionWarn = {
  ...smallActionBase,
  background: "#ea580c",
};

const smallActionBrand = {
  ...smallActionBase,
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
};

const smallActionGhost = {
  ...smallActionBase,
  background: "#ffffff",
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
};

const emptyState = {
  minHeight: 220,
  display: "grid",
  placeItems: "center",
  gap: 10,
  textAlign: "center",
  padding: "2rem 1rem",
};

const emptyStateIcon = {
  width: 48,
  height: 48,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  background: "#fff7ed",
  color: COLORS.warn,
  fontSize: 20,
};

const toastBase = {
  position: "fixed",
  right: 18,
  bottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0.85rem 0.95rem",
  borderRadius: 14,
  boxShadow: "0 18px 26px rgba(15,23,42,.14)",
  fontWeight: 800,
  zIndex: 90,
};

const toastSuccess = {
  ...toastBase,
  background: "#ecfdf5",
  color: "#166534",
  border: "1px solid #bbf7d0",
};

const toastError = {
  ...toastBase,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const deniedWrap = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  padding: 16,
};

const deniedCard = {
  width: "100%",
  maxWidth: 420,
  padding: "1.4rem",
  textAlign: "center",
  borderRadius: 18,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  boxShadow: "0 18px 30px rgba(15,23,42,.08)",
};
